import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { unescape } from 'lodash';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import {
  useCurrentUser,
  useForumPostAdminUpdate,
  useForumPostCreate,
  useForumPostUpdate,
} from '../../../hooks';
import { forum_post_path } from '../../../routes';
import { autocompleteEntities, createEntity } from '../../../utils/backend-api/entities';
import {
  FORUM_POSTS_KEY,
  ForumPost,
  NewForumPost as NewForumPostType,
} from '../../../utils/backend-api/forumPosts';
import { appendSchemeToUrlString, optionalSchemeURLRegex } from '../../../utils/url_validations';
import {
  CheckBoxField,
  MultiValueAutocompleteField,
  TextAreaField,
  TextField,
} from '../../FormFields';
import { EnableProfileModal } from '../../shared';
import styles from './styles.module.scss';

const validationSchema = () =>
  Yup.object({
    title: Yup.string().required('A title is required'),
    body: Yup.string().required('A post is required'),
    external_url: Yup.string().matches(optionalSchemeURLRegex, 'The url must be valid'),
  });

export default function NewForumPostForm({
  initialValues,
  onCreateSuccess,
  isManaging,
}: {
  initialValues: NewForumPostType;
  onCreateSuccess: (forumPost: ForumPost) => void;
  isManaging;
}) {
  const { currentUser } = useCurrentUser();
  const isEditing = !!initialValues.id;
  const { mutateAsync: updatePostAsync } = useForumPostUpdate();
  const { mutateAsync: updateAdminPostAsync } = useForumPostAdminUpdate();
  const { mutateAsync: createPostAsync } = useForumPostCreate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canComment = currentUser?.permitted_to_comment && currentUser?.can_actually_comment;
  const [canInitiallyComment] = useState(canComment);
  const buttonClasses = clsx(styles.submitButton, 'button button-red button-small');
  const buttonText = isEditing ? 'Update' : 'Post';

  initialValues.body = unescape(initialValues.body.replace(/<br ?\/>/g, '\n'));

  const handleSubmit = async (values) => {
    const data: ForumPost = {
      ...values,
      external_url: appendSchemeToUrlString(values.external_url),
      manual_entities: values?.manual_entities.map((entity) => entity.value),
    };

    if (isEditing) {
      const updateAction = isManaging ? updateAdminPostAsync : updatePostAsync;
      return updateAction(data, {
        onSuccess: (data: ForumPost) => {
          toast.success('Post has been updated!');
          queryClient.invalidateQueries(FORUM_POSTS_KEY);
          navigate(forum_post_path(data.id));
          window.scrollTo(0, 0);
        },
        onError: (e) => {
          console.error(e);
          toast.error('Post was not updated, please try again.');
        },
      });
    }

    return createPostAsync(data, {
      onSuccess: (data: ForumPost) => {
        queryClient.invalidateQueries(FORUM_POSTS_KEY);
        onCreateSuccess(data);
      },
      onError: (e) => {
        console.error(e);
        toast.error('Post was not created, please try again.');
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {({ isSubmitting, submitForm }) => (
        <Form className="form forum-post-form">
          {!isManaging && (
            <>
              <TextField name="title" label="Title" placeholder="Type Title" />
              <TextAreaField
                name="body"
                label="Post"
                autoComplete="off"
                placeholder="Type Text"
                minRows={6}
              />
              <TextField name="external_url" placeholder="Type URL (Optional)" label="Link" />
            </>
          )}
          <MultiValueAutocompleteField
            placeholder="Type tags"
            label="Tags"
            name="manual_entities"
            fetchMethod={(value) =>
              autocompleteEntities({
                fragment: value,
                entity_sources: ['manual', 'topic', 'google'],
                order_entities_by: ['is_important', 'pdl_verified'],
              })
            }
            createMethod={(value) =>
              createEntity({
                name: value,
                entity_type: 'other',
                word_type: 'common',
              })
            }
          />
          <div className={styles.formActions}>
            <div className={styles.formActionsDigest}>
              {!isEditing && (
                <CheckBoxField
                  name="email_digest_enabled"
                  label="Include in daily forum post email digest"
                  size="large"
                />
              )}
            </div>
            {canComment && (
              <button type="submit" className={buttonClasses} disabled={isSubmitting}>
                {buttonText}
              </button>
            )}
            {!canInitiallyComment && (
              // TODO: Is there a better message for this?
              <div className={canComment ? 'hidden' : ''}>
                {!currentUser?.permitted_to_comment ? (
                  'Missing name / username, or not permitted to comment'
                ) : (
                  <EnableProfileModal
                    isLoggedIn={currentUser}
                    isSubscriber={!currentUser?.is_free_edition_user}
                    isTriggeredByComment={true}
                    buttonClasses={buttonClasses}
                    commentButtonText={buttonText}
                    isCommentForForum={true}
                    handleSubmitComment={submitForm}
                  />
                )}
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
}
